import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WithdrawPayload } from '@monorepo/client-types';
import { PAYOUT_MINIMUM, PayoutSource } from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';
import { calculatePayout } from '../utils';

interface UsePayoutFormProps {
  maxWithdrawal: number;
  credits: number;
  userPayoutPercentage: number;
  source: PayoutSource;
}

export const usePayoutForm = ({
  maxWithdrawal,
  credits,
  userPayoutPercentage,
  source,
}: UsePayoutFormProps) => {
  const { t } = useTranslation(['withdrawFunds']);

  const {
    dataStore: { userStore },
  } = useStore();

  const form = useForm<WithdrawPayload>({
    mode: 'onChange',
    defaultValues: {
      amount: +maxWithdrawal.toFixed(2),
      agreedToTerms: false,
      metadata: {
        email: userStore.currentUser.email,
        riseId: userStore.currentUser.paymentData?.rise?.riseId,
      },
    },
  });

  const selectedPaymentOption = form.watch('paymentOption');
  const selectedAmount = form.watch('amount');

  const { whatUserGets } = calculatePayout(
    credits,
    selectedAmount,
    maxWithdrawal,
    userPayoutPercentage,
    source,
  );

  const handleMinimumValidation = (value: number): string | true => {
    const { minimumBalanceLegit } = calculatePayout(
      credits,
      value,
      maxWithdrawal,
      userPayoutPercentage,
      source,
    );

    if (minimumBalanceLegit) {
      return true;
    }
    return `${t('withdrawFunds:minimum_balance_error', {
      payoutMinimum: PAYOUT_MINIMUM,
    })}`;
  };

  const isSubmitDisabled =
    !form.formState.isValid || Object.keys(form.formState.errors).length > 0;

  return {
    form,
    selectedPaymentOption,
    selectedAmount,
    whatUserGets,
    handleMinimumValidation,
    isSubmitDisabled,
  };
};
